import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import marketplaceService from '../../../services/marketplaceService';

const MyAuctions = ({ onRefresh }) => {
  const { user } = useAuth();
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadAuctions();
  }, [filter]);

  const loadAuctions = async () => {
    setLoading(true);
    const statusFilter = filter === 'all' ? null : filter;
    const { data, error } = await marketplaceService?.getUserAuctions(user?.id, statusFilter);
    if (!error) {
      setAuctions(data || []);
    }
    setLoading(false);
  };

  const handleCancelAuction = async (auctionId) => {
    if (!confirm('Are you sure you want to cancel this auction?')) return;

    const { error } = await marketplaceService?.cancelAuction(auctionId);
    if (!error) {
      alert('Auction cancelled successfully');
      loadAuctions();
      onRefresh?.();
    } else {
      alert('Failed to cancel auction: ' + error);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      scheduled: { color: 'bg-blue-500/10 text-blue-600', label: 'Scheduled' },
      active: { color: 'bg-green-500/10 text-green-600', label: 'Active' },
      ended: { color: 'bg-gray-500/10 text-gray-600', label: 'Ended' },
      sold: { color: 'bg-purple-500/10 text-purple-600', label: 'Sold' },
      cancelled: { color: 'bg-red-500/10 text-red-600', label: 'Cancelled' }
    };
    const badge = badges?.[status] || badges?.scheduled;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge?.color}`}>
        {badge?.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        {[1, 2, 3]?.map(i => (
          <div key={i} className="bg-card border border-border rounded-lg h-32" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filter Tabs */}
      <div className="flex space-x-2 border-b border-border">
        {['all', 'active', 'scheduled', 'ended', 'sold']?.map(status => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors capitalize ${
              filter === status
                ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {status}
          </button>
        ))}
      </div>

      {/* Auctions List */}
      {auctions?.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Icon name="Gavel" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold text-foreground mb-2">No auctions found</h3>
          <p className="text-muted-foreground">You haven't created any auctions yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {auctions?.map(auction => (
            <div
              key={auction?.id}
              className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start space-x-4">
                {/* Image */}
                <div className="w-24 h-24 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                  {auction?.featured_image_url || auction?.collectible?.primary_image_url ? (
                    <img
                      src={auction?.featured_image_url || auction?.collectible?.primary_image_url}
                      alt={auction?.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon name="Image" size={32} className="text-muted-foreground" />
                    </div>
                  )}
                </div>

                {/* Details */}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-1">{auction?.title}</h3>
                      <div className="flex items-center space-x-2">
                        {getStatusBadge(auction?.status)}
                        {auction?.auction_type === 'premiere' && (
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-500/10 text-yellow-600 flex items-center space-x-1">
                            <Icon name="Star" size={12} />
                            <span>Premiere</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Current Bid</p>
                      <p className="text-lg font-bold text-primary">
                        ${parseFloat(auction?.current_bid || auction?.starting_bid || 0)?.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Bids</p>
                      <p className="text-lg font-semibold text-foreground">{auction?.bid_count || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Start Date</p>
                      <p className="text-sm text-foreground">
                        {new Date(auction?.start_date)?.toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">End Date</p>
                      <p className="text-sm text-foreground">
                        {new Date(auction?.end_date)?.toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-3">
                    {auction?.status === 'scheduled' && (
                      <Button
                        size="sm"
                        variant="outline"
                        iconName="X"
                        iconPosition="left"
                        onClick={() => handleCancelAuction(auction?.id)}
                      >
                        Cancel Auction
                      </Button>
                    )}
                    {auction?.status === 'sold' && auction?.winner_id && (
                      <div className="flex items-center space-x-2 text-sm text-green-600">
                        <Icon name="CheckCircle" size={16} />
                        <span>Sold for ${parseFloat(auction?.winning_bid)?.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyAuctions;